import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Loader2 } from 'lucide-react';
import QuestionsEditor from 'shared/decision/QuestionsEditor';
import RouteTable from 'shared/decision/RouteTable';
import { fromApi, toApi } from 'shared/decision/questions';
import { listDecisionIntegrations, decide } from 'shared/decision/decisionApi';
import { listModelCatalog } from 'features/evals/api/evalsApi';
import { getSelectedTeamNumericId } from 'shared/utils/selectedTeamId';
import { usePermissions, PERMISSIONS } from 'shared/hooks/usePermissions';
import { syncRoutes, explainDecision } from 'features/agents/WorkflowEditor/decideConfig';
import DecideResult from 'features/agents/runs/DecideResult';
import { providerLabel } from 'shared/components/ui/ProviderLogo';
import {
  EmptyHint, ExpressionField, Field, FieldError, NumberField, Notice, SelectField, TextArea,
  cx, primaryBtnCls, useFieldContext, useIssue,
} from 'features/agents/WorkflowEditor/fields/inputs';

/**
 * The Decide node's settings: Model, State, Questions, Can't tell, Routes,
 * then Test. Each is a section that folds, so a node with many questions
 * still leaves its routes in reach. The questions, route table and answers
 * are the shared decision components the playground and evals use.
 */

/** Viewers get no write controls; outside a team (tests, previews) the panel decides. */
function useCanWrite() {
  try { return usePermissions().can(PERMISSIONS.APP_USE); } catch (_) { return true; }
}

export function useDecideReadOnly() {
  const { readOnly } = useFieldContext();
  const canWrite = useCanWrite();
  return !!readOnly || !canWrite;
}

export function DecideSection({ title, meta, issue, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="border-b border-[color:var(--border-default)] pb-3 last:border-b-0" aria-label={title}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex h-7 w-full items-center gap-1.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]"
      >
        <ChevronRight className={cx('h-3.5 w-3.5 shrink-0 text-[color:var(--text-muted)] transition-transform duration-150', open && 'rotate-90')} aria-hidden />
        <span className="text-[12px] font-semibold text-[color:var(--text-primary)]">{title}</span>
        {meta ? <span className="ml-auto truncate text-[11px] text-[color:var(--text-muted)]">{meta}</span> : null}
      </button>
      {open ? <div className="mt-2 space-y-2">{children}</div> : null}
      {!open && issue ? <FieldError>{issue}</FieldError> : null}
    </section>
  );
}

/* ── Model ───────────────────────────────────────────────────────────────── */

const modelCatalogCache = new Map();
function catalogFor(teamId, provider) {
  const key = `${teamId ?? ''}:${provider}`;
  if (!modelCatalogCache.has(key)) {
    modelCatalogCache.set(key, listModelCatalog(teamId, provider).catch((e) => { modelCatalogCache.delete(key); throw e; }));
  }
  return modelCatalogCache.get(key);
}

/**
 * Which decision model the step asks: one of the team's Jev (or Trodo)
 * integrations, and a model from that provider's catalogue. No model means
 * the integration's default.
 *
 * value: { credential_id, model? }
 */
export function DecisionModelField({ value, onChange, bare = false }) {
  const readOnly = useDecideReadOnly();
  const issue = useIssue('model');
  const model = value && typeof value === 'object' ? value : {};
  const { team } = useParams();
  const [rows, setRows] = useState(null);
  const [catalog, setCatalog] = useState(null);

  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let off = false;
    setLoadFailed(false);
    Promise.resolve(listDecisionIntegrations(getSelectedTeamNumericId()))
      .then((r) => { if (!off) setRows(Array.isArray(r) ? r : []); })
      // A failed load is not "no key": say so and offer a retry.
      .catch(() => { if (!off) { setRows(null); setLoadFailed(true); } });
    return () => { off = true; };
  }, [attempt]);

  const selected = (rows || []).find((r) => String(r.id) === String(model.credential_id)) || null;
  const provider = selected?.provider || null;

  useEffect(() => {
    if (!provider) { setCatalog(null); return undefined; }
    let off = false;
    catalogFor(getSelectedTeamNumericId(), provider)
      .then((c) => { if (!off) setCatalog(c || null); })
      .catch(() => { if (!off) setCatalog(null); });
    return () => { off = true; };
  }, [provider]);

  const set = (patch) => onChange({ ...model, ...patch });
  const defaultModel = selected?.default_model || catalog?.default_model || '';
  const models = (catalog?.models || []).filter((m) => m.status !== 'coming_soon');
  const unavailable = model.credential_id && rows && !selected;

  const body = (
    <>
      {loadFailed ? (
        <EmptyHint>
          <span>
            Could not load models.{' '}
            <button type="button" className="text-[color:var(--text-primary)] underline" onClick={() => setAttempt((n) => n + 1)}>Retry</button>
          </span>
        </EmptyHint>
      ) : rows === null ? (
        <EmptyHint>Loading</EmptyHint>
      ) : rows.length === 0 ? (
        <EmptyHint>
          <span>
            Add a Jev or Hartley key in{' '}
            <Link to={`/${encodeURIComponent(team || '')}/settings/models`} className="text-[color:var(--text-primary)] underline">Settings → Models</Link>
          </span>
        </EmptyHint>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Field label="Provider">
            <SelectField
              ariaLabel="Decision model provider"
              value={model.credential_id ? String(model.credential_id) : ''}
              onChange={(id) => set({ credential_id: id || null, model: undefined })}
              options={rows.map((r) => ({ value: String(r.id), label: r.label ? `${providerLabel(r.provider)} · ${r.label}` : providerLabel(r.provider) }))}
              placeholder="Choose"
              disabled={readOnly}
              invalid={!!issue}
            />
          </Field>
          <Field label="Model">
            <SelectField
              ariaLabel="Decision model"
              value={model.model || ''}
              onChange={(m) => set({ model: m || undefined })}
              options={models.map((m) => ({ value: m.id, label: m.label || m.id }))}
              placeholder={defaultModel ? `Default · ${defaultModel}` : 'Default'}
              disabled={readOnly || !selected}
            />
          </Field>
        </div>
      )}
      {unavailable ? <FieldError>Model removed. Choose another.</FieldError> : null}
      {issue && !unavailable ? <FieldError>{issue}</FieldError> : null}
    </>
  );
  if (bare) return <div className="space-y-2">{body}</div>;
  return (
    <DecideSection title="Model" meta={selected ? (model.model || defaultModel) : null} issue={issue}>
      {body}
    </DecideSection>
  );
}

/* ── State ───────────────────────────────────────────────────────────────── */

export function DecideStateField({ value, onChange }) {
  const readOnly = useDecideReadOnly();
  return (
    <DecideSection title="State">
      <ExpressionField
        fieldKey="state"
        value={value || ''}
        onChange={onChange}
        readOnly={readOnly}
        placeholder="Step input"
      />
    </DecideSection>
  );
}

/* ── Questions ───────────────────────────────────────────────────────────── */

/** Same map, same string: keys sorted at every depth (jsonb reorders them). */
function canonical(v) {
  if (v == null || typeof v !== 'object') return JSON.stringify(v ?? null);
  if (Array.isArray(v)) return `[${v.map(canonical).join(',')}]`;
  return `{${Object.keys(v).sort().map((k) => `${JSON.stringify(k)}:${canonical(v[k])}`).join(',')}}`;
}

/**
 * The questions, as editor rows held here (stable uids, so a rename is a
 * rename) and written to the config as the API map. A change from outside
 * (another node opened, a version restored) resets the rows; our own writes
 * do not. Renaming a question or an option carries the route conditions
 * along; deleting one drops its conditions.
 */
export function DecideQuestionsField({ value, cfg, onBatch }) {
  const readOnly = useDecideReadOnly();
  const issue = useIssue('questions');
  const [rows, setRows] = useState(() => fromApi(value || {}));
  const last = useRef(canonical(value || {}));

  useEffect(() => {
    const k = canonical(value || {});
    if (k !== last.current) {
      last.current = k;
      setRows(fromApi(value || {}));
    }
  }, [value]);

  const change = (next) => {
    const questions = toApi(next);
    last.current = canonical(questions);
    const routes = syncRoutes(rows, next, cfg.routes);
    setRows(next);
    onBatch(routes === cfg.routes ? { questions } : { questions, routes });
  };

  return (
    <DecideSection title="Questions" meta={rows.length ? String(rows.length) : null} issue={issue}>
      <QuestionsEditor rows={rows} onChange={change} readOnly={readOnly} showProblems={!readOnly} />
      {issue && !rows.length ? <FieldError>{issue}</FieldError> : null}
    </DecideSection>
  );
}

/* ── Can't tell ──────────────────────────────────────────────────────────── */

export function DecideAbstainField({ cfg, onBatch }) {
  const readOnly = useDecideReadOnly();
  // Missing means on: a node saved before the default existed reads as allowed.
  const on = cfg.allow_abstain !== false;
  return (
    <DecideSection title="Can't tell" meta={on ? 'Allowed' : 'Off'} defaultOpen={on}>
      <label className="flex items-center gap-2 text-[12px] text-[color:var(--text-primary)]">
        <input
          type="checkbox"
          checked={on}
          disabled={readOnly}
          onChange={(e) => onBatch({ allow_abstain: e.target.checked })}
        />
        Allow can't tell
      </label>
      {on ? (
        <Field label="Min confidence" fieldKey="min_confidence">
          <NumberField
            value={cfg.min_confidence ?? ''}
            onChange={(v) => onBatch({ min_confidence: v === '' || v == null ? null : Math.min(1, Math.max(0, Number(v))) })}
            placeholder="0.6"
            min={0}
            max={1}
            step={0.05}
            disabled={readOnly}
            aria-label="Min confidence"
          />
        </Field>
      ) : null}
    </DecideSection>
  );
}

/* ── Routes ──────────────────────────────────────────────────────────────── */

export function DecideRoutesField({ value, cfg, onChange }) {
  const readOnly = useDecideReadOnly();
  const issue = useIssue('routes');
  const table = value || { routes: [], otherwise: { name: 'Otherwise' } };
  const n = (table.routes || []).length + (table.otherwise ? 1 : 0);
  return (
    <DecideSection title="Routes" meta={n ? String(n) : null} issue={issue}>
      <RouteTable questions={cfg.questions || {}} value={table} onChange={onChange} readOnly={readOnly} />
      {issue ? <FieldError>{issue}</FieldError> : null}
    </DecideSection>
  );
}

/* ── Result and Test ─────────────────────────────────────────────────────── */

/**
 * Ask the model with a sample state, without running the agent: the answers,
 * and the route this table would take for them.
 */
export function DecideTest({ cfg }) {
  const readOnly = useDecideReadOnly();
  const [state, setState] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const questions = useMemo(() => cfg.questions || {}, [cfg.questions]);
  const credentialId = cfg.model?.credential_id;
  const ready = !!credentialId && Object.keys(questions).length > 0 && !!state.trim();

  if (readOnly) return null;

  const run = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const r = await decide(getSelectedTeamNumericId(), {
        integration_id: credentialId,
        ...(cfg.model?.model ? { model: cfg.model.model } : {}),
        state,
        questions,
        allow_abstain: cfg.allow_abstain !== false,
        ...(cfg.allow_abstain !== false && cfg.min_confidence != null ? { min_confidence: cfg.min_confidence } : {}),
      });
      setResult(r);
    } catch (e) {
      setError(e?.message || 'The model did not answer.');
    } finally {
      setBusy(false);
    }
  };

  const picked = result ? explainDecision(questions, cfg.routes, result.answers) : null;

  return (
    <DecideSection title="Test" defaultOpen={false}>
      <TextArea
        rows={4}
        value={state}
        onChange={setState}
        placeholder="Sample state"
        aria-label="Sample state"
        drop={false}
        onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); run(); } }}
      />
      <div className="flex items-center justify-end">
        <button type="button" className={primaryBtnCls} disabled={!ready || busy} onClick={run}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
          Run
        </button>
      </div>
      {error ? <Notice tone="error">{error}</Notice> : null}
      {result && picked ? (
        <DecideResult questions={questions} route={picked.name} because={picked.because} answers={result.answers} verb="Would take" />
      ) : null}
    </DecideSection>
  );
}
